import { Avatar, type AvatarShape, type AvatarSize } from "@cntrl/ui";
import { useAvatarUrl } from "../../app/hooks/useAvatar.js";

/**
 * A BAND's identity avatar — the data-aware sibling of the presentational `Avatar`, and the band counterpart
 * of {@link import("./PersonAvatar.js").PersonAvatar}. Give it the band + its `avatarMediaId` (the
 * existence/cache-bust signal every band-carrying DTO exposes) and it resolves the picture through the
 * tenant-less global avatar route (`useAvatarUrl("band", …)` — an authenticated fetch → object URL), falling
 * back to the initials the caller derived.
 *
 * **This is the ONE way to render a band photo.** A band picture is public identity — looked at from OUTSIDE
 * the tenant that owns it (a friend's list, the Top 8, an org weighing a link) — so it cannot ride the
 * band-scoped media route (which 403s for a band the viewer does not reach) nor the server-resolved
 * `photoUrl` (a public CDN URL that is null wherever no public media host is configured, i.e. all of local
 * dev). The global `/avatars/band/{id}` route is the only mechanism that works for reachable and unreachable
 * bands alike, on every lane. Lives in the APP, not `@cntrl/ui`, because it owns a data fetch.
 *
 * A band is a ROUNDED SQUARE, never a circle — the person/band shape split the whole app follows; pass
 * `shape` only to override that, which almost nothing should.
 */
export function BandAvatar({
  bandId,
  avatarMediaId,
  initials,
  size,
  shape,
  className,
}: {
  bandId: string | null | undefined;
  avatarMediaId: string | null | undefined;
  /** Pre-computed, like `Avatar` — callers own the derivation (`bandInitials`). */
  initials: string;
  size?: AvatarSize;
  shape?: AvatarShape;
  className?: string;
}) {
  const url = useAvatarUrl("band", bandId, avatarMediaId);
  return (
    <Avatar
      initials={initials}
      src={url}
      size={size}
      shape={shape ?? "rounded"}
      className={className}
    />
  );
}
